"use client";

import { useState } from "react";
import { signIn } from "next-auth/react";
import { useRouter } from "next/navigation";
import Link from "next/link";

export default function SignupPage() {
  const router = useRouter();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch("/v2/api/auth/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Sign up failed");

      // Auto sign-in via manual fetch
      const csrfRes = await fetch("/v2/api/auth/csrf");
      const { csrfToken } = await csrfRes.json();
      const formBody = new URLSearchParams({
        csrfToken,
        email,
        password,
        callbackUrl: "/v2/dashboard",
        json: "true",
      });
      await fetch("/v2/api/auth/callback/credentials", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formBody.toString(),
      });
      window.location.href = "/v2/dashboard";
    } catch (e: any) {
      setError(e.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen blipo-grid-bg flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-baseline gap-1.5 mb-2">
            <h1 className="text-4xl font-bold tracking-tight text-[#FFC107] lowercase leading-none">
              blipo
            </h1>
            <span className="text-[11px] tracking-[0.18em] text-white/40 uppercase font-medium">
              studios
            </span>
          </div>
          <p className="text-sm text-white/50">Create your studio</p>
        </div>

        <div className="blipo-card p-7">
          <h2 className="text-2xl font-bold mb-1">Get started</h2>
          <p className="text-sm text-white/50 mb-6">Set up your avatar workspace</p>

          {error && (
            <div className="mb-4 bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-sm text-red-400">
              {error}
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs text-white/60 font-medium block mb-2">Full name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="w-full bg-[#0D0D0D] border border-white/[0.06] focus:border-[#FFC107] outline-none rounded-lg px-4 py-3 text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-white/60 font-medium block mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@company.com"
                className="w-full bg-[#0D0D0D] border border-white/[0.06] focus:border-[#FFC107] outline-none rounded-lg px-4 py-3 text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-white/60 font-medium block mb-2">
                Password <span className="text-white/30">(min 6 chars)</span>
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full bg-[#0D0D0D] border border-white/[0.06] focus:border-[#FFC107] outline-none rounded-lg px-4 py-3 text-sm"
              />
            </div>

            <button
              type="submit"
              disabled={submitting || !email || !password}
              className="blipo-btn w-full py-3.5 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              {submitting ? "Creating account..." : "Create account →"}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-white/[0.06] text-center">
            <p className="text-xs text-white/40">
              Already have an account?{" "}
              <Link href="/login" className="text-[#FFC107] hover:text-[#FFD24D] font-semibold">
                Sign in
              </Link>
            </p>
          </div>
        </div>

        <p className="text-[10px] text-white/30 text-center mt-6 tracking-wider uppercase">
          Powered by BLIPO Studios
        </p>
      </div>
    </div>
  );
}
